/**
 * GW INSTANT SEARCH
 *
 * Replaces the Searchanise instant search widget with a theme-native popup powered by Shopify predictive search
 * (managed through the Search & Discovery app).
 *
 * - Desktop (> 768px): dropdown anchored under the header search field. When the header field is hidden
 *   (769px-1150px) the theme's search drawer is used and the dropdown anchors under the drawer field.
 * - Mobile (<= 768px): the header search icon opens a full-screen panel with its own search field.
 *
 * Results markup is rendered server-side by sections/gw-instant-search.liquid and fetched with the
 * Section Rendering API, so the popup can use money formatting, metafields (star ratings), SKUs, etc.
 */
(function () {
  'use strict';

  var MOBILE_MEDIA = '(max-width: 768px)';
  var DEBOUNCE_MS = 250;
  var INPUT_SELECTOR = '[data-gwis-input]';

  var root = document.getElementById('gwis-root');
  if (!root) return;

  var content = root.querySelector('.gwis-content');
  var topPanel = root.querySelector('.gwis-mobile-top-panel');
  var mobileInput = root.querySelector('#gwis-mobile-input');
  var mobileForm = root.querySelector('.gwis-mobile-form');
  var mobileBack = root.querySelector('.gwis-close-button-arrow');
  var mobileClear = root.querySelector('.gwis-clear-button');

  var searchUrl = root.dataset.searchUrl || '/search';
  var sectionId = root.dataset.sectionId || 'gw-instant-search';
  var countSectionId = root.dataset.countSectionId || 'gw-instant-search-count';
  var mobileTrigger = root.dataset.mobileTrigger || 'a[aria-controls="search-drawer"]';
  var rootPath = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  var state = {
    open: false,
    mobile: false,
    anchor: null, // the input the desktop dropdown is anchored to
    query: '',
    timer: null,
    controller: null,
    cache: {},
    countCache: {}
  };

  /* ------------------------------------------------------------------------------------------------ helpers */

  function isMobileViewport() {
    return window.matchMedia(MOBILE_MEDIA).matches;
  }

  function resultsNode() {
    return root.querySelector('.gwis-results');
  }

  function setResults(node) {
    var current = resultsNode();
    if (current) current.replaceWith(node);
    else content.appendChild(node);
  }

  function emptyResults() {
    var node = document.createElement('div');
    node.className = 'gwis-results';
    node.id = 'gwis-results';
    setResults(node);
  }

  function suggestUrl(query) {
    return (
      rootPath +
      'search/suggest?q=' +
      encodeURIComponent(query) +
      '&section_id=' +
      encodeURIComponent(sectionId) +
      '&resources[type]=product,query,collection' +
      '&resources[limit]=6' +
      '&resources[limit_scope]=each' +
      '&resources[options][unavailable_products]=last' +
      '&resources[options][fields]=title,product_type,variants.title,vendor,variants.sku,tag,body'
    );
  }

  // Shopify's AI (semantic) search answers a plain query with hundreds of loosely related products, even for nonsense
  // words. Quoting each word makes the results page keyword-match like the predictive search. A lone token containing
  // a digit is a SKU: it already matches exactly and quoting can break it. Keep in sync with the inline script in
  // sections/gw-search-results.liquid.
  function strictQuery(query) {
    query = (query || '').trim().replace(/\s+/g, ' ');
    if (query === '' || query.indexOf('"') !== -1 || query.indexOf(':') !== -1) return query;
    var words = query.split(' ').filter(function (word) {
      return /[0-9A-Za-zÀ-￿]/.test(word);
    });
    if (words.length === 0 || (words.length === 1 && /\d/.test(words[0]))) return query;
    return words
      .map(function (word) {
        return '"' + word + '"';
      })
      .join(' ');
  }

  function countUrl(query) {
    return rootPath + 'search?type=product&q=' + encodeURIComponent(strictQuery(query)) + '&section_id=' + encodeURIComponent(countSectionId);
  }

  function resultsPageUrl(query) {
    return searchUrl + '?q=' + encodeURIComponent(strictQuery(query));
  }

  function updateAria(input, expanded) {
    if (!input) return;
    input.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    if (expanded) input.setAttribute('aria-controls', 'gwis-results');
  }

  /* ------------------------------------------------------------------------------------------------ fetching */

  function fetchResults(query) {
    if (state.controller) state.controller.abort();
    state.controller = new AbortController();
    var signal = state.controller.signal;

    root.classList.add('gwis--loading');

    var resultsPromise = state.cache[query]
      ? Promise.resolve(state.cache[query])
      : fetch(suggestUrl(query), { signal: signal, headers: { Accept: 'text/html' } })
          .then(function (response) {
            if (!response.ok) throw new Error('gwis suggest ' + response.status);
            return response.text();
          })
          .then(function (html) {
            var doc = new DOMParser().parseFromString(html, 'text/html');
            var node = doc.querySelector('.gwis-results');
            var markup = node ? node.outerHTML : '<div class="gwis-results"></div>';
            state.cache[query] = markup;
            return markup;
          });

    var countPromise = state.countCache[query] !== undefined
      ? Promise.resolve(state.countCache[query])
      : fetch(countUrl(query), { signal: signal, headers: { Accept: 'text/html' } })
          .then(function (response) {
            return response.ok ? response.text() : '';
          })
          .then(function (html) {
            var doc = new DOMParser().parseFromString(html, 'text/html');
            var node = doc.querySelector('[data-gwis-results-count]');
            var count = node ? parseInt(node.textContent, 10) : NaN;
            state.countCache[query] = isNaN(count) ? null : count;
            return state.countCache[query];
          })
          .catch(function (error) {
            if (error.name === 'AbortError') throw error;
            return null;
          });

    resultsPromise
      .then(function (markup) {
        if (signal.aborted || query !== state.query) return;
        var template = document.createElement('template');
        template.innerHTML = markup.trim();
        var node = template.content.firstElementChild;
        node.id = 'gwis-results';
        var viewAll = node.querySelector('#gwis-view-all-link');
        if (viewAll) viewAll.href = searchUrl + '?type=product&q=' + encodeURIComponent(strictQuery(query));
        setResults(node);
        root.classList.remove('gwis--loading');
        show();
        position();
        return countPromise.then(function (count) {
          if (signal.aborted || query !== state.query) return;
          var target = root.querySelector('[data-gwis-count]');
          if (target) target.textContent = count !== null && count > 0 ? count + ' ' : '';
        });
      })
      .catch(function (error) {
        if (error.name === 'AbortError') return;
        root.classList.remove('gwis--loading');
        if (window.console) console.warn('[gw-instant-search]', error);
      });
  }

  function search(query, immediate) {
    query = (query || '').trim();
    state.query = query;
    clearTimeout(state.timer);

    if (query === '') {
      if (state.controller) state.controller.abort();
      root.classList.remove('gwis--loading');
      emptyResults();
      if (!state.mobile) hide();
      return;
    }

    state.timer = setTimeout(function () {
      fetchResults(query);
    }, immediate ? 0 : DEBOUNCE_MS);
  }

  /* ------------------------------------------------------------------------------------------------ show / hide */

  function show() {
    if (state.open) return;
    state.open = true;
    root.hidden = false;
    document.body.classList.add('gwis-open');
    updateAria(state.anchor, true);
  }

  function hide() {
    if (!state.open) return;
    state.open = false;
    root.hidden = true;
    root.classList.remove('gwis--mobile');
    document.body.classList.remove('gwis-open');
    document.documentElement.classList.remove('gwis-mobile-open');
    root.style.cssText = '';
    content.style.maxHeight = '';
    updateAria(state.anchor, false);
    updateAria(mobileInput, false);
    state.mobile = false;
  }

  function close() {
    clearTimeout(state.timer);
    if (state.controller) state.controller.abort();
    root.classList.remove('gwis--loading');
    hide();
  }

  /* ------------------------------------------------------------------------------------------------ desktop */

  function openDesktop(input) {
    state.mobile = false;
    state.anchor = input;
    root.classList.remove('gwis--mobile');
    var query = input.value.trim();
    if (query === '') {
      close();
      return;
    }
    search(query, state.cache[query] !== undefined);
  }

  function isInFixedContainer(element) {
    var node = element;
    while (node && node !== document.body) {
      var pos = window.getComputedStyle(node).position;
      if (pos === 'fixed') return true;
      node = node.parentElement;
    }
    return false;
  }

  function position() {
    if (!state.open || state.mobile || !state.anchor) return;

    var input = state.anchor;
    var rect = input.getBoundingClientRect();
    var fixed = isInFixedContainer(input);
    var viewportWidth = window.innerWidth;
    var viewportHeight = window.innerHeight;

    root.style.position = fixed ? 'fixed' : 'absolute';
    root.style.width = '';
    var width = root.offsetWidth;

    // Center the dropdown under the field, keep a 5% (of dropdown width) gutter from the viewport edges
    var left = rect.left + rect.width / 2 - width / 2;
    var gutter = width * 0.05;
    if (left + width > viewportWidth - gutter) left = viewportWidth - width - gutter;
    if (left < gutter) left = gutter;
    if (width + gutter * 2 > viewportWidth) {
      width = Math.floor(viewportWidth * 0.9);
      root.style.width = width + 'px';
      left = (viewportWidth - width) / 2;
    }

    var topInViewport = rect.bottom;
    var maxHeight = Math.max(200, viewportHeight - topInViewport - 6);

    root.style.left = Math.round(left + (fixed ? 0 : window.pageXOffset)) + 'px';
    root.style.top = Math.round(topInViewport + (fixed ? 0 : window.pageYOffset)) + 'px';
    root.style.maxHeight = maxHeight + 'px';
    content.style.maxHeight = maxHeight + 'px';
  }

  /* ------------------------------------------------------------------------------------------------ mobile */

  function openMobile(initialQuery) {
    state.mobile = true;
    state.anchor = null;
    root.classList.add('gwis--mobile');
    root.style.cssText = '';
    content.style.maxHeight = '';
    show();
    document.documentElement.classList.add('gwis-mobile-open');
    mobileInput.value = initialQuery || '';
    syncMobileButton();
    updateAria(mobileInput, true);
    emptyResults();
    window.requestAnimationFrame(function () {
      mobileInput.focus();
    });
    if (mobileInput.value.trim() !== '') search(mobileInput.value, true);
  }

  function syncMobileButton() {
    var hasValue = mobileInput.value.trim() !== '';
    mobileClear.classList.toggle('gwis-icon-search', !hasValue);
    mobileClear.setAttribute('aria-label', hasValue ? 'Clear' : 'Submit');
  }

  function submitQuery(query) {
    query = (query || '').trim();
    if (query === '') return;
    window.location.href = resultsPageUrl(query);
  }

  /* ------------------------------------------------------------------------------------------------ keyboard */

  function focusableItems() {
    return Array.prototype.slice.call(
      root.querySelectorAll('.gwis-suggestion, .gwis-category, .gwis-product, .gwis-view-all-link')
    );
  }

  function moveHighlight(direction) {
    var items = focusableItems();
    if (!items.length) return;
    var index = items.findIndex(function (item) {
      return item.classList.contains('gwis-over');
    });
    items.forEach(function (item) {
      item.classList.remove('gwis-over');
    });
    index += direction;
    if (index < 0) index = items.length - 1;
    if (index >= items.length) index = 0;
    items[index].classList.add('gwis-over');
    if (typeof items[index].scrollIntoView === 'function') {
      items[index].scrollIntoView({ block: 'nearest' });
    }
  }

  function activateItem(item) {
    if (!item) return false;
    if (item.classList.contains('gwis-suggestion')) {
      submitQuery(item.dataset.gwisQuery || item.textContent);
      return true;
    }
    var link = item.matches('a') ? item : item.querySelector('a');
    if (link) {
      window.location.href = link.href;
      return true;
    }
    return false;
  }

  function onInputKeydown(event) {
    if (!state.open) {
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      close();
      return;
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      moveHighlight(event.key === 'ArrowDown' ? 1 : -1);
      return;
    }
    if (event.key === 'Enter') {
      var highlighted = root.querySelector('.gwis-over');
      if (highlighted && activateItem(highlighted)) {
        event.preventDefault();
      }
    }
  }

  /* ------------------------------------------------------------------------------------------------ bindings */

  function bindInput(input) {
    if (input.dataset.gwisBound) return;
    input.dataset.gwisBound = 'true';
    input.setAttribute('autocomplete', 'off');

    input.addEventListener('focus', function () {
      if (isMobileViewport()) {
        openMobile(input.value);
        return;
      }
      openDesktop(input);
    });

    input.addEventListener('input', function () {
      if (isMobileViewport()) {
        if (!state.mobile) openMobile(input.value);
        return;
      }
      state.anchor = input;
      state.mobile = false;
      search(input.value);
    });

    input.addEventListener('keydown', onInputKeydown);

    var form = input.closest('form');
    if (form && !form.dataset.gwisBound) {
      form.dataset.gwisBound = 'true';
      form.addEventListener('submit', function (event) {
        // Always go through resultsPageUrl() so the query is submitted as "q" (even if the visible field carries
        // another name) and keyword-matched (see strictQuery)
        event.preventDefault();
        submitQuery(input.value);
      });
      form.addEventListener('reset', function () {
        window.setTimeout(function () {
          search('', true);
        }, 0);
      });
    }
  }

  function bindAllInputs() {
    document.querySelectorAll(INPUT_SELECTOR).forEach(bindInput);
  }

  // Mobile: the header search icon opens the full-screen panel instead of the theme drawer
  document.addEventListener(
    'click',
    function (event) {
      var trigger = event.target.closest(mobileTrigger);
      if (!trigger || !isMobileViewport()) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      openMobile('');
    },
    true
  );

  // Mobile panel controls
  mobileInput.addEventListener('input', function () {
    syncMobileButton();
    search(mobileInput.value);
  });
  mobileInput.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      close();
      return;
    }
    onInputKeydown(event);
  });
  mobileInput.addEventListener('focus', function () {
    topPanel.classList.add('gwis-focused');
  });
  mobileInput.addEventListener('blur', function () {
    topPanel.classList.remove('gwis-focused');
  });
  mobileForm.addEventListener('submit', function (event) {
    event.preventDefault();
    submitQuery(mobileInput.value);
  });
  mobileBack.addEventListener('click', function () {
    close();
  });
  mobileClear.addEventListener('click', function () {
    if (mobileInput.value.trim() === '') {
      mobileInput.focus();
      return;
    }
    mobileInput.value = '';
    syncMobileButton();
    search('', true);
    mobileInput.focus();
  });

  // Results interactions (delegated, results markup is replaced on every search)
  root.addEventListener('click', function (event) {
    var suggestion = event.target.closest('.gwis-suggestion');
    if (suggestion) {
      event.preventDefault();
      submitQuery(suggestion.dataset.gwisQuery || suggestion.textContent);
      return;
    }
    var product = event.target.closest('.gwis-product');
    if (product && !event.target.closest('a')) {
      activateItem(product);
    }
  });
  root.addEventListener('keydown', function (event) {
    if (event.key === 'Enter' && event.target.classList.contains('gwis-suggestion')) {
      event.preventDefault();
      submitQuery(event.target.dataset.gwisQuery || event.target.textContent);
    }
  });
  root.addEventListener('mouseover', function (event) {
    var item = event.target.closest('.gwis-suggestion, .gwis-category, .gwis-product, .gwis-view-all-link');
    if (!item) return;
    root.querySelectorAll('.gwis-over').forEach(function (el) {
      el.classList.remove('gwis-over');
    });
  });

  // Close on outside click (desktop dropdown) and on Escape anywhere
  document.addEventListener('mousedown', function (event) {
    if (!state.open || state.mobile) return;
    if (root.contains(event.target)) return;
    if (state.anchor && (event.target === state.anchor || state.anchor.contains(event.target))) return;
    close();
  });
  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape' && state.open) close();
  });

  // Keep the dropdown anchored on scroll / resize, close when switching to mobile
  var repositionRaf = null;
  function scheduleReposition() {
    if (!state.open) return;
    if (repositionRaf) return;
    repositionRaf = window.requestAnimationFrame(function () {
      repositionRaf = null;
      if (!state.open) return;
      if (state.mobile) {
        // The full-screen panel is closed if the viewport grows into desktop sizes
        if (!isMobileViewport()) close();
        return;
      }
      if (isMobileViewport() || (state.anchor && !state.anchor.isConnected)) {
        close();
        return;
      }
      position();
    });
  }
  window.addEventListener('resize', scheduleReposition);
  window.addEventListener('scroll', scheduleReposition, { passive: true });
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(scheduleReposition);

  // When the theme search drawer closes, close the dropdown with it
  var drawer = document.getElementById('search-drawer');
  if (drawer) {
    new MutationObserver(function () {
      if (!drawer.hasAttribute('open') && state.open && state.anchor && drawer.contains(state.anchor)) close();
    }).observe(drawer, { attributes: true, attributeFilter: ['open'] });
  }

  // Inputs may be re-rendered by the theme editor / section reloads
  bindAllInputs();
  var bindRaf = null;
  new MutationObserver(function () {
    if (bindRaf) return;
    bindRaf = window.requestAnimationFrame(function () {
      bindRaf = null;
      bindAllInputs();
    });
  }).observe(document.documentElement, { childList: true, subtree: true });
  document.addEventListener('shopify:section:load', bindAllInputs);
})();
